import { fg } from '@atlaskit/platform-feature-flags/fg';

import { isBinaryContentType } from '../../custom-ui/bridge/methods/useProductFetchClient';
import type { MakeBridgeContext } from '../types';
import type { TBridgeMethod } from './types';

export type FetchProductPayload = {
	contextAri?: string;
	fetchRequestInit: Omit<RequestInit, 'body'> & { body?: string | null };
	isMultipartFormData: boolean;
	product: 'jira' | 'confluence';
	restPath: string;
};

export type FetchProductResponse = {
	body?: string;
	headers: Record<string, string>;
	isAttachment?: boolean;
	status: number;
	statusText: string;
};

type FetchProductContext = MakeBridgeContext<never, 'productFetchClient'>;

/**
 * Makes an authenticated REST call to an Atlassian product API (Jira or Confluence)
 * on behalf of the Forge app running inside the iframe.
 *
 * Delegates to the `productFetchClient` service, which handles:
 * - OAuth token acquisition and caching
 * - Anonymous access fallback (when the app supports it)
 * - Multipart form data encoding/decoding
 * - 3LO consent prompt (when OAuth consent is required)
 *
 * The raw `Response` is serialised into a plain object so it can be sent across
 * the bridge (postMessage boundary): headers are extracted as a plain record,
 * the body is read as text, and binary content types are flagged via `isAttachment`.
 *
 * @param data.restPath - The REST API path (e.g. `/rest/api/3/issue`)
 * @param data.product - The target product: `'jira'` or `'confluence'`
 * @param data.fetchRequestInit - RequestInit options (method, headers, body, etc.)
 * @param data.isMultipartFormData - When true, the client handles multipart encoding
 * @param data.contextAri - Optional installation context ARI to route the request to
 */
export const fetchProduct: TBridgeMethod<
	FetchProductContext,
	FetchProductPayload,
	FetchProductResponse
> = async (
	{ services: { productFetchClient } },
	{ restPath, product, fetchRequestInit, isMultipartFormData, contextAri },
) => {
	const response = fg('forge-ui-request-product-context-ari')
		? await productFetchClient(restPath, product, fetchRequestInit, isMultipartFormData, contextAri)
		: await productFetchClient(restPath, product, fetchRequestInit, isMultipartFormData);

	const { status, statusText } = response;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const headers = Object.fromEntries((response.headers as any).entries());
	const isAttachment = isBinaryContentType(response.headers.get('content-type'));
	const body = response.body ? await response.text() : undefined;

	return { headers, status, statusText, body, isAttachment };
};
